import { useEffect, useMemo, useRef, useState } from "react";
import type { Worker } from "../../trabalhadores/trabalhadoresApi";
import type { WorkOption } from "../../obras/obrasApi";
import { normalizeSearchText } from "../../components/SearchableSelect";

// Assistente de lançamento de horas para o telefone: passos numerados, botões grandes e um só
// gesto por decisão — quem, que dia, quantas horas, em que obra. O CTA fixo em baixo diz sempre
// o que falta. Só se monta em ecrãs pequenos (md:hidden no pai); o desktop mantém o formulário
// clássico.

export interface MobileTimesheetValues {
  workedOn: string;
  hours: number;
  workId: number | null;
  locationText: string | null;
  notes: string | null;
}

export interface RecentTimesheetItem {
  id: number;
  title: string;
  subtitle: string;
  hoursLabel: string;
  validated: boolean;
}

interface FolhaHorasMobileFormProps {
  canManageOthers: boolean;
  workers: Worker[];
  selectedWorkerId: string;
  onSelectWorker: (id: string) => void;
  works: WorkOption[];
  recent: RecentTimesheetItem[];
  isSaving: boolean;
  onSubmit: (values: MobileTimesheetValues) => Promise<boolean>;
}

const FREE_WORK = "__free__";

// Componentes LOCAIS da data (não toISOString): "hoje" e "ontem" são conceitos do calendário do
// utilizador — a variante UTC do desktop (toInput) mudava de dia entre a meia-noite local e a UTC.
const toLocalDayInput = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const cardClass = "bg-card border border-border p-4";
const cardStyle = { borderRadius: "var(--radius-card)" } as const;

const bigButtonClass =
  "border border-border bg-background text-foreground text-sm min-h-[44px] px-3 py-2";

const selectedStyle = {
  borderColor: "var(--primary)",
  backgroundColor: "var(--accent)",
} as const;

const radioDotStyle = (selected: boolean) =>
  selected
    ? { backgroundColor: "var(--primary)", borderColor: "var(--primary)" }
    : { backgroundColor: "var(--muted)" };

function StepTitle({ number, children }: { number: number; children: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span
        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-primary-foreground"
        style={{
          backgroundColor: "var(--primary)",
          fontWeight: "var(--font-weight-semi-bold)",
        }}
        aria-hidden="true"
      >
        {number}
      </span>
      <h2
        className="text-foreground"
        style={{
          fontSize: "var(--text-section-title)",
          fontWeight: "var(--font-weight-semi-bold)",
        }}
      >
        {children}
      </h2>
    </div>
  );
}

export function FolhaHorasMobileForm({
  canManageOthers,
  workers,
  selectedWorkerId,
  onSelectWorker,
  works,
  recent,
  isSaving,
  onSubmit,
}: FolhaHorasMobileFormProps) {
  const [workerSearch, setWorkerSearch] = useState("");
  const [dayChoice, setDayChoice] = useState<"hoje" | "ontem" | "outro">("hoje");
  const [customDate, setCustomDate] = useState("");
  const [hours, setHours] = useState(8);
  const [workChoice, setWorkChoice] = useState("");
  const [workSearch, setWorkSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [savedNotice, setSavedNotice] = useState<string | null>(null);

  // A folga por baixo do conteúdo tem de acompanhar a altura REAL da barra fixa: com zoom ou
  // texto grande a linha de dica quebra em duas e uma folga fixa deixava a barra a tapar a lista.
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(130);
  useEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setBarHeight(bar.offsetHeight));
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  // A confirmação de gravação vive uns segundos e sai sozinha — sem isto, o reset dos passos
  // voltava a pôr a dica "falta escolher…" por cima dela e ninguém chegava a ver o sucesso.
  useEffect(() => {
    if (savedNotice === null) return;
    const handle = setTimeout(() => setSavedNotice(null), 5000);
    return () => clearTimeout(handle);
  }, [savedNotice]);

  const workerStep = canManageOthers ? 1 : 0;
  const stepCount = 3 + workerStep;

  const workedOn = useMemo(() => {
    if (dayChoice === "hoje") return toLocalDayInput(new Date());
    if (dayChoice === "ontem") {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return toLocalDayInput(yesterday);
    }
    return customDate;
  }, [dayChoice, customDate]);

  const chosenDayLabel = useMemo(() => {
    if (!workedOn) return null;
    const formatted = new Date(`${workedOn}T00:00:00`).toLocaleDateString("pt-PT");
    if (dayChoice === "hoje") return `hoje, ${formatted}`;
    if (dayChoice === "ontem") return `ontem, ${formatted}`;
    return formatted;
  }, [workedOn, dayChoice]);

  const visibleWorkers = useMemo(() => {
    // Insensível a acentos, como o seletor partilhado: "Antonio" encontra "António".
    const term = normalizeSearchText(workerSearch.trim());
    if (term === "") return workers;
    return workers.filter((worker) =>
      normalizeSearchText(worker.name).includes(term),
    );
  }, [workers, workerSearch]);

  const visibleWorks = useMemo(() => {
    const term = normalizeSearchText(workSearch.trim());
    const sorted = works
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "pt-PT"));
    if (term === "") return sorted;
    return sorted.filter((work) =>
      normalizeSearchText(`${work.code ?? ""} ${work.name}`).includes(term),
    );
  }, [works, workSearch]);

  const freeText = workSearch.trim();

  // A obra escolhida por extenso, mesmo quando a pesquisa a esconde da lista — senão o realce
  // desaparecia e o utilizador julgava que já não havia escolha (procura em `works`, não nas visíveis).
  const chosenWorkLabel = useMemo(() => {
    if (workChoice === "") return null;
    if (workChoice === FREE_WORK) return freeText === "" ? null : freeText;
    const work = works.find((item) => String(item.id) === workChoice);
    if (!work) return null;
    return work.code ? `${work.code} — ${work.name}` : work.name;
  }, [workChoice, freeText, works]);

  // O que falta, pela ordem dos passos — o CTA em baixo diz sempre o próximo gesto.
  const missing = useMemo(() => {
    if (canManageOthers && !selectedWorkerId) {
      return { hint: "Falta escolher quem trabalhou (passo 1)", cta: "Escolha o trabalhador" };
    }
    if (!workedOn) {
      return {
        hint: `Falta escolher o dia (passo ${1 + workerStep})`,
        cta: "Escolha o dia",
      };
    }
    // O `max` do calendário só limita o picker — quem escreve a data à mão passava por ele.
    if (workedOn > toLocalDayInput(new Date())) {
      return {
        hint: "O dia não pode ser no futuro",
        cta: "Corrija o dia",
      };
    }
    if (hours <= 0) {
      return {
        hint: `Faltam as horas (passo ${2 + workerStep})`,
        cta: "Indique as horas",
      };
    }
    if (workChoice === "" || (workChoice === FREE_WORK && freeText === "")) {
      return {
        hint: `Falta escolher a obra (passo ${3 + workerStep})`,
        cta: "Escolha a obra",
      };
    }
    return null;
  }, [canManageOthers, selectedWorkerId, workedOn, hours, workChoice, freeText, workerStep]);

  const save = async () => {
    if (missing || isSaving) return;
    const ok = await onSubmit({
      workedOn,
      hours,
      workId: workChoice === FREE_WORK ? null : Number(workChoice),
      locationText: workChoice === FREE_WORK ? freeText : null,
      notes: notes.trim() !== "" ? notes.trim() : null,
    });
    if (ok) {
      // Recomeça limpo: o próximo lançamento parte do zero, sem seleções herdadas.
      if (canManageOthers) onSelectWorker("");
      setWorkerSearch("");
      setDayChoice("hoje");
      setCustomDate("");
      setHours(8);
      setWorkChoice("");
      setWorkSearch("");
      setNotes("");
      setSavedNotice("Horas guardadas.");
    }
  };

  const stepHours = (delta: number) =>
    setHours((prev) => Math.min(24, Math.max(0.5, Math.round((prev + delta) * 2) / 2)));

  return (
    <div className="space-y-4">
      <p className="text-sm text-secondary-text">
        Marque as horas em {stepCount} passos.
      </p>

      {canManageOthers && (
        <section className={cardClass} style={cardStyle}>
          <StepTitle number={1}>Quem trabalhou?</StepTitle>
          <input
            type="search"
            aria-label="Procurar trabalhador"
            placeholder="Procurar trabalhador"
            className={`${bigButtonClass} w-full`}
            style={{ borderRadius: "var(--radius)" }}
            value={workerSearch}
            onChange={(event) => setWorkerSearch(event.target.value)}
          />
          <div
            className="mt-2 space-y-2 overflow-y-auto"
            style={{ maxHeight: "310px" }}
            aria-label="Trabalhador"
          >
            {visibleWorkers.map((worker) => {
              const selected = selectedWorkerId === String(worker.id);
              return (
                <button
                  key={worker.id}
                  type="button"
                  aria-pressed={selected}
                  className={`${bigButtonClass} w-full flex items-center gap-2`}
                  style={{
                    borderRadius: "var(--radius)",
                    ...(selected ? selectedStyle : null),
                  }}
                  onClick={() => onSelectWorker(selected ? "" : String(worker.id))}
                >
                  <span
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full text-primary-foreground"
                    style={{
                      backgroundColor: selected ? "var(--primary)" : "var(--muted-foreground)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                    aria-hidden="true"
                  >
                    {worker.name.trim().charAt(0).toUpperCase()}
                  </span>
                  <span
                    className="flex-1 text-left"
                    style={{ fontWeight: "var(--font-weight-medium)" }}
                  >
                    {worker.name}
                  </span>
                  <span
                    className="w-5 h-5 rounded-full border border-border"
                    style={radioDotStyle(selected)}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
            {visibleWorkers.length === 0 && (
              <p className="text-sm text-secondary-text px-1 py-2">
                Nenhum trabalhador com esse nome.
              </p>
            )}
          </div>
        </section>
      )}

      <section className={cardClass} style={cardStyle}>
        <StepTitle number={1 + workerStep}>Que dia?</StepTitle>
        <div className="flex gap-2 flex-wrap">
          {(
            [
              ["hoje", "Hoje"],
              ["ontem", "Ontem"],
              ["outro", "Outro dia"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`${bigButtonClass} flex-1`}
              style={{
                borderRadius: "var(--radius)",
                ...(dayChoice === value ? selectedStyle : null),
              }}
              aria-pressed={dayChoice === value}
              onClick={() => setDayChoice(value)}
            >
              {label}
            </button>
          ))}
        </div>
        {dayChoice === "outro" && (
          <input
            type="date"
            aria-label="Dia"
            className={`${bigButtonClass} w-full mt-2`}
            style={{ borderRadius: "var(--radius)" }}
            value={customDate}
            max={toLocalDayInput(new Date())}
            onChange={(event) => setCustomDate(event.target.value)}
          />
        )}
        {chosenDayLabel && (
          <p className="text-sm text-secondary-text mt-2">
            Dia escolhido:{" "}
            <span className="text-foreground" style={{ fontWeight: "var(--font-weight-medium)" }}>
              {chosenDayLabel}
            </span>
          </p>
        )}
      </section>

      <section className={cardClass} style={cardStyle}>
        <StepTitle number={2 + workerStep}>Quantas horas?</StepTitle>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className={bigButtonClass}
            style={{ borderRadius: "var(--radius)", minWidth: "64px", fontSize: "1.5rem" }}
            aria-label="Menos meia hora"
            onClick={() => stepHours(-0.5)}
          >
            −
          </button>
          <div className="flex-1 text-center" role="status">
            <div
              className="text-foreground"
              style={{ fontSize: "2.5rem", fontWeight: "var(--font-weight-semi-bold)", lineHeight: 1.1 }}
            >
              {hours.toLocaleString("pt-PT")}
            </div>
            <div className="text-sm text-secondary-text">horas</div>
          </div>
          <button
            type="button"
            className={bigButtonClass}
            style={{
              borderRadius: "var(--radius)",
              minWidth: "64px",
              fontSize: "1.5rem",
              // Só a moldura leva o laranja da marca: o glifo em laranja sobre o fundo claro
              // ficava a 2,84:1, abaixo do mínimo AA de 3:1 para texto grande.
              borderColor: "var(--primary)",
            }}
            aria-label="Mais meia hora"
            onClick={() => stepHours(0.5)}
          >
            +
          </button>
        </div>
        <div className="flex gap-2 mt-3">
          {[4, 6, 8].map((preset) => (
            <button
              key={preset}
              type="button"
              className={`${bigButtonClass} flex-1`}
              style={{
                borderRadius: "var(--radius)",
                ...(hours === preset ? selectedStyle : null),
              }}
              aria-pressed={hours === preset}
              onClick={() => setHours(preset)}
            >
              {preset}h
            </button>
          ))}
        </div>
      </section>

      <section className={cardClass} style={cardStyle}>
        <StepTitle number={3 + workerStep}>Em que obra?</StepTitle>
        <input
          type="search"
          aria-label="Procurar ou escrever a obra"
          placeholder="Procurar ou escrever a obra"
          className={`${bigButtonClass} w-full`}
          style={{ borderRadius: "var(--radius)" }}
          value={workSearch}
          onChange={(event) => setWorkSearch(event.target.value)}
        />
        {chosenWorkLabel && (
          <p className="text-sm text-secondary-text mt-2">
            Obra escolhida:{" "}
            <span className="text-foreground" style={{ fontWeight: "var(--font-weight-medium)" }}>
              {chosenWorkLabel}
            </span>
          </p>
        )}
        <div
          className="mt-2 space-y-2 overflow-y-auto"
          style={{ maxHeight: "310px" }}
          aria-label="Obra"
        >
          {visibleWorks.map((work) => {
            const value = String(work.id);
            const selected = workChoice === value;
            return (
              <button
                key={work.id}
                type="button"
                aria-pressed={selected}
                className={`${bigButtonClass} w-full flex items-center gap-2`}
                style={{
                  borderRadius: "var(--radius)",
                  ...(selected ? selectedStyle : null),
                }}
                onClick={() => setWorkChoice(selected ? "" : value)}
              >
                <span
                  className="flex-1 text-left"
                  style={{ fontWeight: "var(--font-weight-medium)" }}
                >
                  {work.code ? `${work.code} — ${work.name}` : work.name}
                </span>
                <span
                  className="w-5 h-5 rounded-full border border-border"
                  style={radioDotStyle(selected)}
                  aria-hidden="true"
                />
              </button>
            );
          })}
          {freeText !== "" && (
            <button
              type="button"
              aria-pressed={workChoice === FREE_WORK}
              className={`${bigButtonClass} w-full flex items-center gap-2`}
              style={{
                borderRadius: "var(--radius)",
                ...(workChoice === FREE_WORK ? selectedStyle : null),
              }}
              onClick={() => setWorkChoice((prev) => (prev === FREE_WORK ? "" : FREE_WORK))}
            >
              <span className="flex-1 text-left">
                Usar «{freeText}» como local
              </span>
              <span
                className="w-5 h-5 rounded-full border border-border"
                style={radioDotStyle(workChoice === FREE_WORK)}
                aria-hidden="true"
              />
            </button>
          )}
          {visibleWorks.length === 0 && freeText === "" && (
            <p className="text-sm text-secondary-text px-1 py-2">
              Sem obras para mostrar.
            </p>
          )}
        </div>
      </section>

      <details className={cardClass} style={cardStyle}>
        <summary
          className={`${bigButtonClass} block`}
          style={{
            borderRadius: "var(--radius)",
            fontWeight: "var(--font-weight-medium)",
            cursor: "pointer",
          }}
        >
          Escrever o que foi feito (opcional)
        </summary>
        <textarea
          aria-label="O que foi feito"
          rows={3}
          className={`${bigButtonClass} w-full mt-2`}
          style={{ borderRadius: "var(--radius)" }}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </details>

      {recent.length > 0 && (
        <div className="space-y-2">
          <h2
            className="text-sm text-secondary-text"
            style={{ fontWeight: "var(--font-weight-medium)" }}
          >
            Últimos registos no período
          </h2>
          <ul className="space-y-2">
            {recent.map((item) => (
              <li
                key={item.id}
                className="bg-card border border-border p-3 flex items-center gap-3 text-sm"
                style={{ borderRadius: "var(--radius)" }}
              >
                <div className="flex-1">
                  <div style={{ fontWeight: "var(--font-weight-medium)" }}>{item.title}</div>
                  <div className="text-secondary-text">{item.subtitle}</div>
                </div>
                <div
                  className="text-foreground"
                  style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                >
                  {item.hoursLabel}
                </div>
                <span
                  className="inline-flex items-center px-2 py-0.5 text-xs"
                  style={{
                    borderRadius: "var(--radius)",
                    backgroundColor: item.validated ? "var(--accent)" : "var(--muted)",
                    color: item.validated ? "var(--foreground)" : "var(--muted-foreground)",
                  }}
                >
                  {item.validated ? "Validada" : "Pendente"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Folga com a altura REAL da barra fixa (medida), para nada ficar tapado — com zoom ou
          texto grande a dica quebra em duas linhas e uma folga fixa deixava de chegar. */}
      <div style={{ height: `${barHeight + 12}px` }} aria-hidden="true" />

      <div
        ref={barRef}
        className="fixed bottom-0 left-0 right-0 z-20 bg-card border-t border-border p-4 md:hidden"
        style={{ boxShadow: "0 -2px 8px rgba(0,0,0,0.06)" }}
      >
        <div role="status" className="text-sm text-secondary-text text-center mb-2">
          {savedNotice ?? (missing ? missing.hint : "Tudo pronto — reveja e guarde.")}
        </div>
        <button
          type="button"
          className="w-full text-primary-foreground disabled:opacity-50"
          style={{
            borderRadius: "var(--radius)",
            backgroundColor: missing ? "var(--muted)" : "var(--primary)",
            color: missing ? "var(--muted-foreground)" : undefined,
            minHeight: "52px",
            // 20px a bold: texto "grande" para o contraste AA (3:1) do branco sobre o laranja da
            // marca — a 18px semi-bold o limiar aplicável era 4,5:1 e o par ficava aquém.
            fontSize: "1.25rem",
            fontWeight: 700,
          }}
          disabled={missing !== null || isSaving}
          aria-busy={isSaving}
          onClick={save}
        >
          {isSaving ? "A guardar…" : missing ? missing.cta : "Guardar as horas"}
        </button>
      </div>
    </div>
  );
}
